import React, { useState } from 'react';
import { Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { INITIAL_DUE_DILIGENCE } from '../formQuestionsEnum';
import FormTextField from '../FormFields/FormTextField';
import FieldContainer from '../FormFields/FieldContainer';
import FormTitle from '../FormFields/FormTitle';
import FormAutocomplete from '../FormFields/FormAutocomplete';
import FormPhoneNumberField from '../FormFields/FormPhoneNumField';
import FormEinField from '../FormFields/FormEinField';
import FormRoutingNumber from '../FormFields/FormRoutingNumber';
import { validatePositiveNumber, validateZip, validateEmail, validateAddress } from '../ValidateFunctions';

FacilityForm.propTypes = {
  permissions: PropTypes.object,
  isAdmin: PropTypes.bool,
};

export default function FacilityForm({ permissions, isAdmin }) {
  const [focusedField, setFocusedField] = useState(null);
  const {
    data: { fiftyStates, facilityStructures },
  } = useSelector((state) => state.transitions);
  const { watch } = useFormContext();

  const fieldIds = [
    INITIAL_DUE_DILIGENCE.FACILITY_NAME.id,
    INITIAL_DUE_DILIGENCE.FACILITY_SHORT_NAME.id,
    INITIAL_DUE_DILIGENCE.FACILITY_DBA.id,
    INITIAL_DUE_DILIGENCE.FACILITY_STRUCTURE.id,
    INITIAL_DUE_DILIGENCE.FACILITY_EIN.id,
    INITIAL_DUE_DILIGENCE.AVERAGE_CENSUS.id,
    INITIAL_DUE_DILIGENCE.BED_COUNT_SNF.id,
    INITIAL_DUE_DILIGENCE.BED_COUNT_ILF.id,
    INITIAL_DUE_DILIGENCE.BED_COUNT_ALF.id,
    INITIAL_DUE_DILIGENCE.BED_COUNT_PC.id,
    INITIAL_DUE_DILIGENCE.FACILITY_ADDRESS.id,
    INITIAL_DUE_DILIGENCE.FACILITY_CITY.id,
    INITIAL_DUE_DILIGENCE.FACILITY_COUNTY.id,
    INITIAL_DUE_DILIGENCE.FACILITY_STATE.id,
    INITIAL_DUE_DILIGENCE.FACILITY_ZIP.id,
    INITIAL_DUE_DILIGENCE.BANK_NAME.id,
    INITIAL_DUE_DILIGENCE.BANK_PHONE_NUMBER.id,
    INITIAL_DUE_DILIGENCE.ROUTING_NUMBER.id,
    INITIAL_DUE_DILIGENCE.BANK_ADDRESS.id,
    INITIAL_DUE_DILIGENCE.BANK_REPRESENTATIVE.id,
    INITIAL_DUE_DILIGENCE.BANK_REPRESENTATIVE_PHONE_NUMBER.id,
    INITIAL_DUE_DILIGENCE.BANK_REPRESENTATIVE_EMAIL.id,
    INITIAL_DUE_DILIGENCE.AP_PHONE_NUMBER.id,
    INITIAL_DUE_DILIGENCE.AP_NAME.id,
    INITIAL_DUE_DILIGENCE.AP_EMAIL.id,
  ];

  const showForm = fieldIds.some((id) => id in permissions) || isAdmin;
  const facilityName = watch('facilityName');

  return (
    showForm && (
      <>
        <Typography sx={{ mb: 1 }} variant="h6" color="info.main">
          {facilityName || 'New Facility'}
        </Typography>
        <Grid container spacing={1.5}>
          {(isAdmin || INITIAL_DUE_DILIGENCE.FACILITY_NAME.id in permissions) && (
            <Grid item xs={12} md={4}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Current facility name' }} isRequired />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.FACILITY_NAME.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.FACILITY_NAME.id] || isAdmin,
                    validation: {
                      required: 'Facility name is required',
                      pattern: {
                        value: /\S/,
                        message: 'Facility name is required',
                      },
                    },
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.FACILITY_SHORT_NAME.id in permissions) && (
            <Grid item xs={12} md={4}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Current facility short name' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.FACILITY_SHORT_NAME.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.FACILITY_SHORT_NAME.id] || isAdmin,
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.FACILITY_DBA.id in permissions) && (
            <Grid item xs={12} md={4}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Current facility DBA' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.FACILITY_DBA.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.FACILITY_DBA.id] || isAdmin,
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.FACILITY_STRUCTURE.id in permissions) && (
            <Grid item xs={12} md={4}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Current facility structure' }} />
                <FormAutocomplete
                  fieldName={INITIAL_DUE_DILIGENCE.FACILITY_STRUCTURE.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.FACILITY_STRUCTURE.id] || isAdmin,
                  }}
                  options={facilityStructures}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.FACILITY_EIN.id in permissions) && (
            <Grid item xs={12} md={4}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Current facility EIN' }} />
                <FormEinField
                  fieldName={INITIAL_DUE_DILIGENCE.FACILITY_EIN.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.FACILITY_EIN.id] || isAdmin,
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.AVERAGE_CENSUS.id in permissions) && (
            <Grid item xs={12} md={4}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Average census' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.AVERAGE_CENSUS.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.AVERAGE_CENSUS.id] || isAdmin,
                    validation: {
                      pattern: { ...validatePositiveNumber },
                    },
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.BED_COUNT_SNF.id in permissions) && (
            <Grid item xs={12} md={3}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Bed count' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.BED_COUNT_SNF.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.BED_COUNT_SNF.id] || isAdmin,
                    validation: {
                      pattern: { ...validatePositiveNumber },
                    },
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.BED_COUNT_ILF.id in permissions) && (
            <Grid item xs={12} md={3}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Bed count ILF' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.BED_COUNT_ILF.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.BED_COUNT_ILF.id] || isAdmin,
                    validation: {
                      pattern: { ...validatePositiveNumber },
                    },
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.BED_COUNT_ALF.id in permissions) && (
            <Grid item xs={12} md={3}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Bed count ALF' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.BED_COUNT_ALF.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.BED_COUNT_ALF.id] || isAdmin,
                    validation: {
                      pattern: { ...validatePositiveNumber },
                    },
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.BED_COUNT_PC.id in permissions) && (
            <Grid item xs={12} md={3}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Bed count PC' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.BED_COUNT_PC.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.BED_COUNT_PC.id] || isAdmin,
                    validation: {
                      pattern: { ...validatePositiveNumber },
                    },
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.FACILITY_ADDRESS.id in permissions) && (
            <Grid item xs={12}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Address' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.FACILITY_ADDRESS.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.FACILITY_ADDRESS.id] || isAdmin,
                    validation: {
                      validate: validateAddress,
                    },
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.FACILITY_CITY.id in permissions) && (
            <Grid item xs={12} md={3}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'City' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.FACILITY_CITY.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.FACILITY_CITY.id] || isAdmin,
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.FACILITY_COUNTY.id in permissions) && (
            <Grid item xs={12} md={3}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'County' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.FACILITY_COUNTY.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.FACILITY_COUNTY.id] || isAdmin,
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.FACILITY_STATE.id in permissions) && (
            <Grid item xs={12} md={3}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'State' }} />
                <FormAutocomplete
                  fieldName={INITIAL_DUE_DILIGENCE.FACILITY_STATE.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.FACILITY_STATE.id] || isAdmin,
                    validation: {},
                  }}
                  options={fiftyStates}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.FACILITY_ZIP.id in permissions) && (
            <Grid item xs={12} md={3}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Zip' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.FACILITY_ZIP.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.FACILITY_ZIP.id] || isAdmin,
                    validation: {
                      pattern: { ...validateZip },
                    },
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.BANK_NAME.id in permissions) && (
            <Grid item xs={12} md={6}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Bank name' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.BANK_NAME.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.BANK_NAME.id] || isAdmin,
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.BANK_PHONE_NUMBER.id in permissions) && (
            <Grid item xs={12} md={6}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Bank phone number' }} />
                <FormPhoneNumberField
                  fieldName={INITIAL_DUE_DILIGENCE.BANK_PHONE_NUMBER.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.BANK_PHONE_NUMBER.id] || isAdmin,
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.ROUTING_NUMBER.id in permissions) && (
            <Grid item xs={12} md={12}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Routing number' }} />
                <FormRoutingNumber
                  fieldName={INITIAL_DUE_DILIGENCE.ROUTING_NUMBER.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.ROUTING_NUMBER.id] || isAdmin,
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.BANK_ADDRESS.id in permissions) && (
            <Grid item xs={12}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Bank address' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.BANK_ADDRESS.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.BANK_ADDRESS.id] || isAdmin,
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.BANK_REPRESENTATIVE.id in permissions) && (
            <Grid item xs={12} md={4}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Bank representative name' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.BANK_REPRESENTATIVE.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.BANK_REPRESENTATIVE.id] || isAdmin,
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.BANK_REPRESENTATIVE_PHONE_NUMBER.id in permissions) && (
            <Grid item xs={12} md={4}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Bank representative phone number' }} />
                <FormPhoneNumberField
                  fieldName={INITIAL_DUE_DILIGENCE.BANK_REPRESENTATIVE_PHONE_NUMBER.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.BANK_REPRESENTATIVE_PHONE_NUMBER.id] || isAdmin,
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.BANK_REPRESENTATIVE_EMAIL.id in permissions) && (
            <Grid item xs={12} md={4}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Bank representative email' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.BANK_REPRESENTATIVE_EMAIL.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.BANK_REPRESENTATIVE_EMAIL.id] || isAdmin,
                    validation: {
                      pattern: { ...validateEmail },
                    },
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.AP_PHONE_NUMBER.id in permissions) && (
            <Grid item xs={12} md={4}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'AP phone number' }} />
                <FormPhoneNumberField
                  fieldName={INITIAL_DUE_DILIGENCE.AP_PHONE_NUMBER.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.AP_PHONE_NUMBER.id] || isAdmin,
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.AP_NAME.id in permissions) && (
            <Grid item xs={12} md={4}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'AP name' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.AP_NAME.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.AP_NAME.id] || isAdmin,
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}

          {(isAdmin || INITIAL_DUE_DILIGENCE.AP_EMAIL.id in permissions) && (
            <Grid item xs={12} md={4}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'AP email' }} />
                <FormTextField
                  fieldName={INITIAL_DUE_DILIGENCE.AP_EMAIL.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[INITIAL_DUE_DILIGENCE.AP_EMAIL.id] || isAdmin,
                    validation: {
                      pattern: { ...validateEmail },
                    },
                  }}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                />
              </FieldContainer>
            </Grid>
          )}
        </Grid>
      </>
    )
  );
}
